/* ─────────────────────────────────────────────
   HEAD MODEL - single source of truth for <head>

   buildHead() returns a plain description of everything that belongs in the
   document head for one (route, language) pair. Two consumers render it:

     1. src/seo/Seo.jsx        → react-helmet-async, at runtime
     2. scripts/prerender.mjs  → static HTML strings, at build time

   Both must produce identical tags, so neither is allowed to invent tags of
   its own - anything new goes here.

   This file is .mjs on purpose: plain Node (the prerender script) imports it
   directly, with no bundler and no transpile step. Keep it free of JSX, React
   and browser globals.
   ───────────────────────────────────────────── */

import {
  LANGS, HTML_LANG, HREFLANG, OG_LOCALE, SITE, urlFor, routeForSlug,
} from '../i18n/lang.mjs';
import {
  routeMeta, getMeta, salonDescription, OG_IMAGE,
} from './routeMeta.mjs';
import { BASIC_COURSE, ADVANCED_COURSE } from '../config/educations.mjs';
import { OFFER, HAIR_OFFER } from '../config/offers.mjs';
import {
  SERVICE_PRICE_RANGE, PRODUCT_PRICE_RANGE, SHOW_PRODUCT_PRICES,
} from '../config/prices.mjs';

/* Static BeautySalon organization data (identical on every page; only the
   description is translated). Mirrors the SEO brief exactly. */
export function salonSchema(lang) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BeautySalon',
    '@id': `${SITE}/#salon`,
    name: 'Vision Beauty',
    description: salonDescription(lang),
    url: `${SITE}/`,
    telephone: '+381653585545',
    email: 'visionbeauty25@gmail.com',
    image: OG_IMAGE,
    priceRange: '$$',
    currenciesAccepted: 'RSD',
    paymentAccepted: 'Gotovina, Platne kartice',
    taxID: '114990674',
    identifier: {
      '@type': 'PropertyValue',
      name: 'Matični broj',
      value: '68007518',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Miljkovačka 1',
      addressLocality: 'Niš',
      postalCode: '18000',
      addressRegion: 'Nišavski okrug',
      addressCountry: 'RS',
    },
    geo: { '@type': 'GeoCoordinates', latitude: 43.345149, longitude: 21.864649 },
    hasMap: 'https://www.google.com/maps?cid=0xeec35ad54ebb4ce',
    areaServed: [
      { '@type': 'City', name: 'Niš' },
      { '@type': 'AdministrativeArea', name: 'Nišavski okrug' },
    ],
    sameAs: ['https://www.instagram.com/visionbeauty.rs/'],
    availableLanguage: ['sr', 'en', 'de'],
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00', closes: '19:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Sunday', opens: '09:00', closes: '17:00',
      },
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Usluge salona Vision Beauty Niš',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Trajna šminka obrva', url: `${SITE}/permanent-makeup` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Vega obrve', url: `${SITE}/vega` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Baby lips - trajna šminka usana', url: `${SITE}/baby-lips` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Profesionalno šminkanje', url: `${SITE}/makeup` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Frizerske usluge', url: `${SITE}/hairdresser` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Manikir i pedikir', url: `${SITE}/nails` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Nadogradnja trepavica', url: `${SITE}/eyelashes` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Depilacija', url: `${SITE}/depilation` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Edukacije i kursevi', url: `${SITE}/educations` } },
      ],
    },
  };
}

/* FAQPage from the 4 questions already on the site (home + contact). */
export function faqSchema(t, lang) {
  const qa = [1, 2, 3, 4].map((i) => ({
    '@type': 'Question',
    name: t(`faq.q${i}`).trim(),
    acceptedAnswer: { '@type': 'Answer', text: t(`faq.a${i}`).trim() },
  }));
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: lang,
    mainEntity: qa,
  };
}

/* FAQPage from a locale array of { q, a } - the course pages render the same
   array, so the markup always matches what the visitor can actually read. */
export function faqSchemaFromKey(t, lang, key) {
  const items = t(key, { returnObjects: true });
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: lang,
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q.trim(),
      acceptedAnswer: { '@type': 'Answer', text: item.a.trim() },
    })),
  };
}

/* ── Course / CourseInstance ───────────────────
   The volatile numbers (start date, group size, price) come from
   src/config/educations.mjs so a new group is a one-file edit.

   `name`, `description` and `teaches` are localised so the markup matches
   the copy a visitor actually reads on that language's page. What stays
   Serbian is `inLanguage` - the course itself is taught in Serbian, whichever
   language the page is written in. */

const COURSE_LOCATION = {
  '@type': 'Place',
  name: 'Vision Beauty',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Miljkovačka 1',
    addressLocality: 'Niš',
    postalCode: '18000',
    addressCountry: 'RS',
  },
};

const COURSE_PROVIDER = {
  '@type': 'Organization',
  '@id': `${SITE}/#salon`,
  name: 'Vision Beauty',
  url: `${SITE}/`,
};

/** The language the courses are actually taught in, regardless of page language. */
const COURSE_LANGUAGE = 'sr-Latn-RS';

const COURSE_CONTENT = {
  '/educations': {
    educationalLevel: 'Beginner',
    sr: {
      name: 'Bazni kurs šminkanja - Od nule do prve mušterije',
      description: 'Četvorodnevni kurs profesionalnog šminkanja u Nišu za početnice, sa petim danom na kojem polaznica radi pravu klijentkinju salona. Uključuje skriptu, sertifikat, foto-sesiju radova i doživotnu online podršku.',
      teaches: [
        'Natural glam šminka',
        'Smokey eyes',
        'Blendani ajlajner',
        'Cut crease',
        'Formiranje cena usluga šminkanja',
        'Higijena radnog mesta',
      ],
    },
    en: {
      name: 'Basic makeup course - From zero to your first client',
      description: 'A four-day professional makeup course in Nis for beginners, with a fifth day on which you work on a real salon client. Includes the workbook, a certificate, a photo session of your work and lifetime online support.',
      teaches: [
        'Natural glam makeup',
        'Smokey eyes',
        'Blended eyeliner',
        'Cut crease',
        'Pricing makeup services',
        'Workplace hygiene',
      ],
    },
    de: {
      name: 'Basis Make-up Kurs - Von null zur ersten Kundin',
      description: 'Ein viertägiger professioneller Make-up Kurs in Nis für Anfängerinnen, mit einem fünften Tag, an dem Sie eine echte Kundin des Salons schminken. Inklusive Skript, Zertifikat, Fotoshooting Ihrer Arbeiten und lebenslanger Online-Unterstützung.',
      teaches: [
        'Natural Glam Make-up',
        'Smokey Eyes',
        'Geblendeter Lidstrich',
        'Cut Crease',
        'Preisgestaltung für Make-up Leistungen',
        'Hygiene am Arbeitsplatz',
      ],
    },
  },
  '/napredni-kurs-sminkanja': {
    educationalLevel: 'Advanced',
    sr: {
      name: 'Napredni kurs šminkanja - Sledeći korak',
      description: 'Dvodnevni individualni kurs u Nišu za šminkere koje već rade: rad na dva kompleksna look-a, konkretan plan cena, snimanje tutorijala i tranzicija i mentorski deo o pokretanju sopstvenog salona.',
      teaches: [
        'Kompleksne tehnike šminkanja',
        'Formiranje i podizanje cena usluga',
        'Snimanje tutorijala i tranzicija',
        'Postavka kamere i svetla',
        'Pokretanje i vođenje sopstvenog salona',
        'Rad sa klijentima',
      ],
    },
    en: {
      name: 'Advanced makeup course - The next step',
      description: 'A two-day one-on-one course in Nis for working makeup artists: two complex looks, a concrete pricing plan, filming tutorials and transitions, and a mentoring session on starting your own salon.',
      teaches: [
        'Advanced makeup techniques',
        'Setting and raising your prices',
        'Filming tutorials and transitions',
        'Camera and lighting setup',
        'Starting and running your own salon',
        'Working with clients',
      ],
    },
    de: {
      name: 'Make-up Aufbaukurs - Der nächste Schritt',
      description: 'Ein zweitägiges Einzelcoaching in Nis für Make-up Artists mit Erfahrung: zwei komplexe Looks, ein konkreter Preisplan, das Filmen von Tutorials und Übergängen sowie ein Mentoring-Gespräch über die Eröffnung eines eigenen Salons.',
      teaches: [
        'Fortgeschrittene Make-up Techniken',
        'Preise festlegen und erhöhen',
        'Tutorials und Übergänge filmen',
        'Kamera- und Lichtaufbau',
        'Einen eigenen Salon eröffnen und führen',
        'Umgang mit Kundinnen',
      ],
    },
  },
};

export function courseSchema(route, lang) {
  const entry = COURSE_CONTENT[route];
  if (!entry) return null;
  const content = entry[lang] || entry.sr;

  const url = urlFor(route, lang);
  const isBasic = route === '/educations';
  const config = isBasic ? BASIC_COURSE : ADVANCED_COURSE;

  const instance = {
    '@type': 'CourseInstance',
    courseMode: 'onsite',
    courseWorkload: config.workload,
    inLanguage: COURSE_LANGUAGE,
    location: COURSE_LOCATION,
  };

  if (isBasic) {
    instance.startDate = BASIC_COURSE.startDate;
    instance.maximumAttendeeCapacity = BASIC_COURSE.maxAttendees;
    instance.offers = {
      '@type': 'Offer',
      price: String(BASIC_COURSE.priceEur),
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      url,
      validFrom: BASIC_COURSE.priceValidFrom,
    };
  } else if (ADVANCED_COURSE.showPrice && ADVANCED_COURSE.priceEur) {
    /* No `offers` at all while the price is private. A price of 0 or an empty
       string is invalid and the Rich Results Test reports it as an error, so
       the whole block stays out until there is a real amount to publish. */
    instance.offers = {
      '@type': 'Offer',
      price: String(ADVANCED_COURSE.priceEur),
      priceCurrency: ADVANCED_COURSE.priceCurrency,
      availability: 'https://schema.org/InStock',
      url,
    };
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: content.name,
    description: content.description,
    url,
    inLanguage: COURSE_LANGUAGE,
    educationalLevel: entry.educationalLevel,
    teaches: content.teaches,
    provider: COURSE_PROVIDER,
    hasCourseInstance: instance,
  };
}

/* ── Service ──────────────────────────────────
   The four hair-service pages. `serviceType` is the untranslated technique
   name so the entity stays recognisable across languages; `name` and
   `description` follow the page copy.

   The offer range is derived from src/config/prices.mjs, from the very same
   numbers the price table on the page prints - schema and table cannot drift. */

const SERVICE_TYPE = {
  '/balayage': 'Balayage',
  '/farbanje-kose': 'Hair colouring',
  '/tretmani-kose': 'Hair treatment',
  '/musko-sisanje': "Men's haircut",
};

export function serviceSchema(route, lang, meta) {
  const range = SERVICE_PRICE_RANGE[route];
  if (!range) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: SERVICE_TYPE[route],
    name: meta.h1,
    description: meta.description,
    url: urlFor(route, lang),
    provider: { '@id': `${SITE}/#salon` },
    areaServed: { '@type': 'City', name: 'Niš' },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'RSD',
      lowPrice: range.low,
      highPrice: range.high,
      availability: 'https://schema.org/InStock',
    },
  };
}

/* ── Product ──────────────────────────────────
   Extensions and wigs are goods, not services. Nothing is emitted while
   SHOW_PRODUCT_PRICES is false.

   That is deliberate rather than lazy: Google needs `offers`, `review` or
   `aggregateRating` before a Product is eligible for a rich result, and a
   Product carrying none of them is flagged by the Rich Results Test. Since
   the stock is only partly in and no price is public, emitting nothing keeps
   the page clean; the block appears the moment prices are filled in. */

const PRODUCT_CATEGORY = {
  '/ekstenzije-za-kosu': 'Hair extensions',
  '/perike': 'Wigs',
};

export function productSchema(route, lang, meta) {
  const range = SHOW_PRODUCT_PRICES ? PRODUCT_PRICE_RANGE[route] : null;
  if (!range) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: meta.h1,
    description: meta.description,
    category: PRODUCT_CATEGORY[route],
    url: urlFor(route, lang),
    brand: { '@type': 'Brand', name: 'Vision Beauty' },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'RSD',
      lowPrice: range.low,
      highPrice: range.high,
      availability: 'https://schema.org/InStock',
      seller: { '@id': `${SITE}/#salon` },
    },
  };
}

/* ── Offer ────────────────────────────────────
   /ponude carries two datable promotions, and each gets its own Offer block:
   the September group of the basic course, and "Posle mora", the after-summer
   colour promotion. The third card on the page - the advanced course - runs on
   no calendar at all: it has a price but no start and no end date, and a
   dateless Offer is what the Rich Results Test flags, so it stays out of the
   markup and lives in the Course schema on its own page instead.

   Every number and every date comes from src/config/offers.mjs and
   src/config/educations.mjs, so `validThrough` follows the group's start date
   instead of being hard-coded. The copy on the page reads from the same
   configs, so the markup can never advertise a date the visitor cannot see. */

const OFFER_CONTENT = {
  sr: {
    name: 'Septembarska grupa kursa šminkanja - Od nule do prve mušterije',
    description: 'Grupa od najviše {{max}} polaznica, početak {{date}}. Prve {{seats}} prijave biraju gratis nokte ili trepavice, po želji.',
    courseName: 'Bazni kurs šminkanja - Od nule do prve mušterije',
  },
  en: {
    name: 'September group of the makeup course - From zero to your first client',
    description: 'A group of at most {{max}} students, starting {{date}}. The first {{seats}} applications choose free nails or lashes, whichever they want.',
    courseName: 'Basic makeup course - From zero to your first client',
  },
  de: {
    name: 'September-Gruppe des Make-up Kurses - Von null zur ersten Kundin',
    description: 'Eine Gruppe von höchstens {{max}} Teilnehmerinnen, Start am {{date}}. Die ersten {{seats}} Anmeldungen wählen gratis Nägel oder Wimpern, ganz nach Wunsch.',
    courseName: 'Basis Make-up Kurs - Von null zur ersten Kundin',
  },
};

/* "Posle mora" - the hair promotion. No price of its own: the colour service
   is charged exactly as the price list says and this rides on top, so the
   block carries priceCurrency and the area served, never a price. */
const HAIR_OFFER_CONTENT = {
  sr: {
    name: 'Posle mora - uz bojenje od {{minSpend}} din: Quick Repair nega i garancija dorade tona',
    description: 'Uz svaku uslugu bojenja od {{minSpend}} RSD - balayage, air touch, pramenove ili blajhanje - Quick Repair nega za kod kuće i besplatna dorada tona u roku od {{days}} dana.',
  },
  en: {
    name: 'After Summer - with colour from {{minSpend}} RSD: Quick Repair care and a tone correction guarantee',
    description: 'With every colour service from {{minSpend}} RSD - balayage, air touch, highlights or bleaching - a take-home Quick Repair treatment and a free tone correction within {{days}} days.',
  },
  de: {
    name: 'Nach dem Sommer - zu Farbbehandlungen ab {{minSpend}} RSD: Quick Repair Pflege und Tonkorrektur-Garantie',
    description: 'Zu jeder Farbbehandlung ab {{minSpend}} RSD - Balayage, Air Touch, Strähnen oder Blondierung - eine Quick Repair Pflege für zu Hause und eine kostenlose Tonkorrektur innerhalb von {{days}} Tagen.',
  },
};

/** Fill {{placeholders}} in a schema string. Kept local to the Offer blocks -
    the page copy goes through i18next, which the schema builder cannot use. */
const fill = (template, vars) => Object.entries(vars).reduce(
  (text, [key, value]) => text.split(`{{${key}}}`).join(value),
  template
);

export function offerSchema(lang) {
  const content = OFFER_CONTENT[lang] || OFFER_CONTENT.sr;
  /* The Serbian label already ends in a full stop ("28. septembar 2026."),
     which would collide with the one the sentence supplies. Strip it here so
     the template alone decides the punctuation. */
  const label = BASIC_COURSE.startDateLabel[lang] || BASIC_COURSE.startDateLabel.sr;
  const vars = {
    max: String(BASIC_COURSE.maxAttendees),
    date: label.replace(/[.]$/, ''),
    seats: String(OFFER.earlyBirdSeats),
  };
  const description = fill(content.description, vars);

  return {
    '@context': 'https://schema.org',
    '@type': 'Offer',
    name: content.name,
    description,
    url: urlFor('/ponude', lang),
    price: String(BASIC_COURSE.priceEur),
    priceCurrency: 'EUR',
    validFrom: OFFER.validFrom,
    validThrough: OFFER.validThrough,
    availability: 'https://schema.org/LimitedAvailability',
    seller: { '@id': `${SITE}/#salon` },
    itemOffered: {
      '@type': 'Course',
      name: content.courseName,
      url: urlFor('/educations', lang),
    },
  };
}

/** The "Posle mora" Offer block. Runs on its own calendar, so validThrough
    comes from HAIR_OFFER and not from the course's start date. */
export function hairOfferSchema(lang) {
  const content = HAIR_OFFER_CONTENT[lang] || HAIR_OFFER_CONTENT.sr;
  const locale = lang === 'en' ? 'en-US' : 'de-DE';
  const vars = {
    minSpend: HAIR_OFFER.minSpendRsd.toLocaleString(locale),
    days: String(HAIR_OFFER.toneGuaranteeDays),
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'Offer',
    name: fill(content.name, vars),
    description: fill(content.description, vars),
    url: urlFor('/ponude', lang),
    priceCurrency: 'RSD',
    validFrom: HAIR_OFFER.validFrom,
    validThrough: HAIR_OFFER.validThrough,
    availability: 'https://schema.org/InStock',
    seller: { '@id': `${SITE}/#salon` },
    areaServed: { '@type': 'City', name: 'Niš' },
    itemOffered: {
      '@type': 'Service',
      name: 'Balayage',
      url: urlFor('/balayage', lang),
    },
  };
}

/* BreadcrumbList (Home > Page) for sub-pages. */
export function breadcrumbSchema(route, lang, meta, homeLabel) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: homeLabel, item: urlFor('/', lang) },
      { '@type': 'ListItem', position: 2, name: meta.h1, item: urlFor(route, lang) },
    ],
  };
}

/**
 * Normalise a pathname (already without the /en, /de prefix) to a route key.
 * Routes with a translated slug come back as their logical (Serbian) key, so
 * /advanced-makeup-course and /fortgeschrittenen-kurs both look up the same
 * routeMeta entry.
 */
export function normalizeRoute(pathname) {
  const p = pathname || '/';
  return routeForSlug(p.length > 1 ? p.replace(/\/+$/, '') : '/');
}

/**
 * Everything that belongs in <head> for one route + language.
 *
 * @param {string} route  logical route, no language prefix ('/', '/service', …)
 * @param {string} lang   'sr' | 'en' | 'de'
 * @param {(key: string) => string} t  translation lookup for `lang`
 * @returns {{
 *   htmlLang: string,
 *   title: string|null,
 *   metas: Array<{name?: string, property?: string, content: string}>,
 *   links: Array<{rel: string, href: string, hrefLang?: string}>,
 *   jsonLd: Array<object>,
 * }}
 */
export function buildHead(route, lang, t) {
  const htmlLang = HTML_LANG[lang];

  // Routes intentionally excluded from SEO (e.g. /faq): keep working but noindex.
  if (!routeMeta[route]) {
    return {
      htmlLang,
      title: null,
      metas: [{ name: 'robots', content: 'noindex, follow' }],
      links: [{ rel: 'canonical', href: urlFor(route, lang) }],
      jsonLd: [],
    };
  }

  const meta = getMeta(route, lang);
  const canonical = urlFor(route, lang);

  const links = [{ rel: 'canonical', href: canonical }];
  // hreflang - reciprocal across all three versions + x-default
  LANGS.forEach((l) => {
    links.push({ rel: 'alternate', hrefLang: HREFLANG[l], href: urlFor(route, l) });
  });
  links.push({ rel: 'alternate', hrefLang: 'x-default', href: urlFor(route, 'sr') });

  const metas = [
    { name: 'description', content: meta.description },
    { property: 'og:site_name', content: 'Vision Beauty' },
    { property: 'og:title', content: meta.title },
    { property: 'og:description', content: meta.description },
    { property: 'og:image', content: OG_IMAGE },
    { property: 'og:url', content: canonical },
    { property: 'og:type', content: meta.type },
    { property: 'og:locale', content: OG_LOCALE[lang] },
    { name: 'twitter:card', content: 'summary_large_image' },
  ];

  const jsonLd = [salonSchema(lang)];
  if (route === '/' || route === '/contact') jsonLd.push(faqSchema(t, lang));

  /* The offers page: BeautySalon + both Offers + BreadcrumbList, nothing else. */
  if (route === '/ponude') {
    jsonLd.push(offerSchema(lang));
    jsonLd.push(hairOfferSchema(lang));
  }

  /* Course + its FAQ, alongside the salon and breadcrumb blocks, never instead. */
  if (route === '/educations') {
    jsonLd.push(courseSchema(route, lang));
    jsonLd.push(faqSchemaFromKey(t, lang, 'educations.basic.faq.items'));
  }
  if (route === '/napredni-kurs-sminkanja') {
    jsonLd.push(courseSchema(route, lang));
    jsonLd.push(faqSchemaFromKey(t, lang, 'advancedCourse.faq.items'));
  }

  /* Hair block: Service on the four service pages, Product on the two product
     pages (suppressed while prices are private), FAQ on all of them.

     /hairdresser is in this map for the FAQ alone - it carries no price table
     of its own, so serviceSchema()/productSchema() both come back null and
     only the FAQPage is added, next to the BeautySalon and BreadcrumbList
     blocks it already had. */
  const HAIR_FAQ_KEY = {
    '/hairdresser': 'hairdresserService.faq.items',
    '/balayage': 'balayagePage.faq.items',
    '/farbanje-kose': 'hairColouring.faq.items',
    '/tretmani-kose': 'hairTreatments.faq.items',
    '/musko-sisanje': 'mensHaircut.faq.items',
    '/ekstenzije-za-kosu': 'hairExtensions.faq.items',
    '/perike': 'wigs.faq.items',
  };
  if (HAIR_FAQ_KEY[route]) {
    const entity = serviceSchema(route, lang, meta) || productSchema(route, lang, meta);
    if (entity) jsonLd.push(entity);
    jsonLd.push(faqSchemaFromKey(t, lang, HAIR_FAQ_KEY[route]));
  }

  if (route !== '/') jsonLd.push(breadcrumbSchema(route, lang, meta, t('breadcrumb.home')));

  return { htmlLang, title: meta.title, metas, links, jsonLd };
}
